// Typed fetch wrapper. All calls send cookies so Better-Auth sessions work.
// Callers should be tolerant to network failures — the frontend has a
// bundled/localStorage fallback for reads.

import type { ZodType } from 'zod';

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Thrown when a response succeeds (2xx + JSON body) but doesn't match the
 * provided Zod schema. Different from ApiError: server didn't say "no", the
 * client just got something it can't make sense of. v2 Phase 1 contract:
 * the API boundary is validated; consumers can assume the schema holds.
 */
export class ApiSchemaError extends Error {
  path: string;
  issues: unknown;
  raw: unknown;
  constructor(path: string, issues: unknown, raw: unknown) {
    super(`Response from ${path} failed schema validation`);
    this.name = 'ApiSchemaError';
    this.path = path;
    this.issues = issues;
    this.raw = raw;
  }
}

interface RequestOpts<T = unknown> extends RequestInit {
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Optional Zod schema applied to the parsed JSON response body. On parse
   * failure: dispatches `ck:sync-fail` and throws ApiSchemaError. When
   * omitted: response is returned as-is (back-compat).
   */
  schema?: ZodType<T>;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export async function api<T = unknown>(path: string, opts: RequestOpts<T> = {}): Promise<T> {
  const { json, query, headers, schema, ...rest } = opts;
  let url = API_BASE + path;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += (path.includes('?') ? '&' : '?') + s;
  }
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  };
  const res = await fetch(url, init);
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = await res.text().catch(() => null); }
    // Notify the admin shell when the server session is gone, so it can
    // redirect to the login form instead of showing a screenful of errors.
    // Skip for the session-probe endpoint itself to avoid a redirect loop.
    if (res.status === 401 && typeof window !== 'undefined' && !path.startsWith('/api/auth/get-session')) {
      window.dispatchEvent(new CustomEvent('ck:auth-expired', { detail: { path, status: res.status } }));
    }
    throw new ApiError(res.status, `API ${res.status} on ${path}`, body);
  }
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    const raw = await res.json();
    if (schema) {
      const parsed = schema.safeParse(raw);
      if (!parsed.success) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ck:sync-fail', {
            detail: { path, scope: 'schema', issues: parsed.error.issues },
          }));
        }
        throw new ApiSchemaError(path, parsed.error.issues, raw);
      }
      return parsed.data as T;
    }
    return raw as T;
  }
  return (await res.text()) as unknown as T;
}

/**
 * Probe the server for an active Better-Auth session. Returns the user object
 * if logged in and the session is valid; null otherwise. Never throws on 401 —
 * it's an expected outcome of "not signed in".
 */
export interface ServerSession {
  user: { id: string; email: string; name?: string | null; role?: string };
  session: unknown;
}

export async function getServerSession(): Promise<ServerSession | null> {
  try {
    const res = await fetch((API_BASE) + '/api/auth/get-session', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as ServerSession | null;
    if (!data || !data.user) return null;
    return data;
  } catch {
    return null;
  }
}

// ─── GET dedup + tiny TTL cache ─────────────────────────────────────────────
//
// Naively, every useResource() mount that hasn't yet read from localStorage
// fires its own fetch. On a fresh home-page load this produced 16+ duplicate
// /api/config/<key> requests (each section calling useConfig with its own
// fallback). The cache below collapses identical concurrent GETs onto a
// single in-flight Promise, and serves duplicate calls within RESULT_TTL_MS
// from memory. Mutations bust the cache for their resource prefix.
//
// Schema note: the cached `value` is the schema-parsed result (when callers
// pass a schema). Same-path callers should pass the same schema; mixing on
// the same path is a developer error.

interface CacheEntry {
  promise: Promise<unknown>;
  // Result snapshot once the promise resolved; cached for RESULT_TTL_MS.
  result?: { value: unknown; cachedAt: number };
}

const RESULT_TTL_MS = 300;
const MAX_ENTRIES = 256;
const cache = new Map<string, CacheEntry>();

function buildCacheKey(path: string, query?: RequestOpts['query']): string {
  if (!query) return `GET ${path}`;
  const pairs: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    pairs.push(`${k}=${String(v)}`);
  }
  pairs.sort();
  return `GET ${path}${pairs.length ? '?' + pairs.join('&') : ''}`;
}

function evictIfFull(): void {
  if (cache.size <= MAX_ENTRIES) return;
  // Drop the oldest insertion — Map preserves insertion order.
  const firstKey = cache.keys().next().value as string | undefined;
  if (firstKey) cache.delete(firstKey);
}

/**
 * Drop every cached GET whose path begins with `prefix`. Called by mutating
 * helpers so a POST/PUT/DELETE on `/api/articles/foo` invalidates both the
 * single-row and collection caches. The match is permissive — `prefix` should
 * be the resource path without query strings.
 */
function bustCacheByPrefix(prefix: string): void {
  if (!prefix) return;
  const needle = `GET ${prefix}`;
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(needle)) cache.delete(key);
  }
  // Fire a window event so module-level caches in sibling files (notably
  // siteData's bulk config map) can clear themselves without us importing
  // them here (would be a cyclic dependency).
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ck:api-cache-bust', { detail: { prefix } }));
  }
}

// Public helper — useful for the admin store + cmsEvents subscribers that need
// to force a reread (e.g., editing a config blob in admin should clear cached
// reads from siteData).
export function bustApiCache(prefix?: string): void {
  if (!prefix) {
    cache.clear();
    return;
  }
  bustCacheByPrefix(prefix);
}

interface GetOpts<T> {
  query?: RequestOpts['query'];
  schema?: ZodType<T>;
}

// Shorthand helpers for common shapes
export function apiGet<T = unknown>(path: string, queryOrOpts?: RequestOpts['query'] | GetOpts<T>): Promise<T> {
  // Back-compat: legacy callers pass `query` directly as the second arg.
  // New callers pass `{ query?, schema? }`.
  const opts: GetOpts<T> = queryOrOpts && (typeof queryOrOpts === 'object') && ('schema' in queryOrOpts || 'query' in queryOrOpts)
    ? (queryOrOpts as GetOpts<T>)
    : { query: queryOrOpts as RequestOpts['query'] };
  const key = buildCacheKey(path, opts.query);
  const cached = cache.get(key);
  if (cached) {
    if (cached.result && Date.now() - cached.result.cachedAt < RESULT_TTL_MS) {
      return Promise.resolve(cached.result.value as T);
    }
    // In-flight request — return the same promise. Concurrent callers share it.
    return cached.promise as Promise<T>;
  }
  const promise = api<T>(path, { method: 'GET', query: opts.query, schema: opts.schema }).then(
    (value) => {
      // Cache the success briefly so a burst of mounts coalesces.
      const entry = cache.get(key);
      if (entry) {
        entry.result = { value, cachedAt: Date.now() };
      }
      return value;
    },
    (err) => {
      // Errors are not cached — drop the entry so the next call retries.
      cache.delete(key);
      throw err;
    },
  );
  cache.set(key, { promise });
  evictIfFull();
  return promise;
}

// Best-effort prefix derived from a write path so we can invalidate the
// collection AND any per-row reads. e.g. `/api/articles/foo` → `/api/articles`.
function writePathPrefix(path: string): string {
  const slash = path.indexOf('/', '/api/'.length);
  return slash === -1 ? path : path.slice(0, slash);
}

export function apiPost<T = unknown>(path: string, json?: unknown, schema?: ZodType<T>): Promise<T> {
  bustCacheByPrefix(writePathPrefix(path));
  return api<T>(path, { method: 'POST', json, schema });
}

export function apiPut<T = unknown>(path: string, json?: unknown, schema?: ZodType<T>): Promise<T> {
  bustCacheByPrefix(writePathPrefix(path));
  return api<T>(path, { method: 'PUT', json, schema });
}

export function apiDelete<T = unknown>(path: string, schema?: ZodType<T>): Promise<T> {
  bustCacheByPrefix(writePathPrefix(path));
  return api<T>(path, { method: 'DELETE', schema });
}
